/**
 * @module lib/commercial/keytar/Template
 */
import merge from "lodash/merge";
import isNil from "lodash/isNil";
import { setAfterLoginCookie } from "lib/identity/api";
import { getProp } from "utils/utils";
import { hasLeadFigure } from "utils/story";

/**
 * Constant enum for different blockout types.
 * Indicates what section of the page is accessible to the user.
 * @enum {String}
 */
export const BLOCKOUT_TYPES = {
  EVERYTHING: "EVERYTHING",
  HEADER_NAV_ACCESSIBLE: "HEADER_NAV_ACCESSIBLE",
  PREVIEW: "PREVIEW",
  NOTHING: "NOTHING"
};

/**
 * Determines if the teaser/preview rendering should be used:
 *  If the `keytarViewType` system prop is set to teaser OR the user is in scope for the Hermes test
 *  then a teaser wall should be shown on content that is not video, gallery or interactive.
 * @param {String} [context=""] - context to be used for view type local storage key name (e.g. regWall), when
 *  the system property setting needs to be different than the default.
 * @returns {Boolean} true if teaser should be displayed
 */
export function shouldShowTeaserRendering(context="") {
  const viewTypeKey = context !== "regWall" ? "tgam.keytar.viewType" : "tgam.keytar.regWallViewType";
  const teaserRenderingEnabled = getProp(window, viewTypeKey, "") === "teaser";
  const pagesubtype = getProp(window, "tgam.meta.pagesubtype");
  const validContent = !["video", "gallery", "interactive"].includes(pagesubtype);

  return validContent ? teaserRenderingEnabled : false;
}

/**
 * Created a template that renders a keytar wall
 * @class
 */
export default class Template {
  /**
   * Creates an instance of Template.
   * @param {Object} [userProps={}] - of the desired property values
   * @param {String} [userProps.blockoutType] - what is accessible to the user
   * @param {Function} [userProps.didRender] - called after render() method
   * @param {Function} [userProps.render] - method that renders template markup
   */
  constructor(userProps = {}) {
    this.parentContext = undefined; // parent Wall instance pointer
    this.additionalAnalytics = null;
    this.defaultProps = {
      blockoutType: BLOCKOUT_TYPES.PREVIEW,
      didRender: this.didRender,
      didRenderClickHandling: this.didRenderClickHandling,
      render: () => {}
    };
    this.props = merge({}, this.defaultProps, userProps);
  }

  /**
   * Sequentially calls the various render methods.
   * Ordered: render(), and didRender().
   * @returns {Promise<Boolean>} true if rendered
   */
  render() {
    return this.props.render.call(this)
      .then(rendered => {
        if (rendered) {
          this.additionalAnalytics = this.props.analytics || {};
          this.props.didRender.call(this);
          this.props.didRenderClickHandling.call(this);
        }
        return rendered;
      });
  }

  /**
   * Checks if there is darwin test running for the paywall and returns it's config.
   * @returns {{ darwinTemplate: String, viewType: String, paragraphs: String }}
   */
  darwinRendering() {
    let darwinTemplate;
    let viewType;
    let paragraphs;
    if (!isNil(this.props.darwinRenderKey)) {
      // We want the default/fallback value to be `undefined` and not `null` here.
      // `undefined` values play better with object destructuring and default method param values.
      darwinTemplate = getProp(window, `tgam.darwin.keytar.${this.props.darwinRenderKey}.template`) || undefined;
      viewType = getProp(window, `tgam.darwin.keytar.${this.props.darwinRenderKey}.viewType`) || undefined;
      paragraphs = getProp(window, `tgam.darwin.keytar.${this.props.darwinRenderKey}.paragraphs`) || undefined;
    }
    return { viewType, darwinTemplate, paragraphs };
  }

  /**
   * Overrides analytics props with values passed from the template if the wall's viewType is teaser
   * @param {Number} [paragraphs=0] - how many paragraph are displayed
   */
  updateTeaserAnalytics(paragraphs = 0) {
    const contentType = getProp(window, "tgam.datalayer.article.pagesubtype");
    let visibleParagraphs = paragraphs;
    if (contentType === "gallery" || (contentType === "longread" && hasLeadFigure())) {
      visibleParagraphs = 0;
    }
    this.props.blockoutType = BLOCKOUT_TYPES.PREVIEW;
    this.props.analytics = {
      viewType: "teaser",
      adsVisible: true,
      countVisibleParagraphs: visibleParagraphs
    };
  }

  /**
   * Overrides analytics props with values passed from the template
   * @param {Object} values - new values to assign to this instance's analytics prop
   */
  updateWallAnalytics(values) {
    this.props.analytics = Object.assign(values);
  }

  /**
   * Called after render() method
   */
  didRender() {
    // nothing - override via userProps
  }

  /**
   * Called after the class didRender() method.
   *
   * Sets the afterLoginGoTo cookie automatically if a clicked element
   * is the child of a ".js-c-keytar-container" and the clicked element contains
   * the class name "js-keytar-algt".
   *
   * If a developer overrides this method it is their responsibility to ensure that
   * the afterLoginGoTo cookie is set accordingly.
   */
  didRenderClickHandling() {
    const takeover = document.querySelector(".js-c-keytar-container");
    if (takeover) {
      takeover.addEventListener("click", (e) => {
        if (e.target.classList.contains("js-keytar-algt")) {
          setAfterLoginCookie();
        }
      });
    }
  }
}
